/* ==========================================================================
   3D Product Showcase — carousel logic
   Vanilla JS, no dependencies. GPU-friendly transforms only.
   ========================================================================== */

(function () {
  'use strict';

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  var ThreeDShowcase = function (root) {
    this.root = root;
    this.stage = root.querySelector('.tds-stage');
    this.track = root.querySelector('.tds-track');
    this.items = Array.prototype.slice.call(root.querySelectorAll('.tds-item'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('.tds-dot'));
    this.prevBtn = root.querySelector('.tds-nav--prev');
    this.nextBtn = root.querySelector('.tds-nav--next');

    var raw = {};
    try {
      raw = JSON.parse(root.dataset.settings || '{}');
    } catch (e) {
      raw = {};
    }

    this.settings = {
      curvature: raw.curvature || 45,
      perspective: raw.perspective || 1400,
      rotationIntensity: raw.rotationIntensity || 28,
      scaleDiff: typeof raw.scaleDiff === 'number' ? raw.scaleDiff : 0.22,
      blurIntensity: raw.blurIntensity || 0,
      speed: raw.speed || 700,
      scrollSensitivity: raw.scrollSensitivity || 4,
      enableScroll: !!raw.enableScroll,
      autoRotate: !!raw.autoRotate,
      autoRotateSpeed: raw.autoRotateSpeed || 5
    };

    this.active = 0;
    this.count = this.items.length;
    this.isAnimating = false;
    this.animLockTimer = null;
    this.autoplayTimer = null;
    this.wheelCooldown = false;
    this.wheelAccum = 0;
    this.touchStartX = null;
    this.touchStartY = null;
    this.inView = false;
    this.atEdgeCooldown = false;

    if (this.count === 0) return;

    this.bindEvents();
    this.observeVisibility();
    this.render(true);

    if (this.settings.autoRotate) {
      this.startAutoplay();
    }
  };

  ThreeDShowcase.prototype.bindEvents = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.go(self.active - 1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.go(self.active + 1);
      });
    }

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.dataset.index, 10);
        self.go(idx);
      });
    });

    this.items.forEach(function (item) {
      item.addEventListener('click', function () {
        var idx = parseInt(item.dataset.index, 10);
        if (idx !== self.active) {
          self.go(idx);
        }
      });
    });

    // Keyboard navigation
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        self.go(self.active - 1);
        self.pauseAutoplay();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        self.go(self.active + 1);
        self.pauseAutoplay();
      }
    });

    // Scroll-driven navigation (desktop wheel)
    if (this.settings.enableScroll) {
      this.stage.addEventListener(
        'wheel',
        function (e) {
          if (!self.inView) return;

          // No more edges to stop at — the carousel loops infinitely,
          // so this always drives the carousel rather than handing off
          // to page scroll.
          e.preventDefault();

          if (self.wheelCooldown) return;

          var goingForward = e.deltaY > 0;

          var threshold = 11 - self.settings.scrollSensitivity;
          self.wheelAccum += Math.abs(e.deltaY) / 30;
          if (self.wheelAccum < threshold) return;
          self.wheelAccum = 0;

          self.wheelCooldown = true;
          self.go(goingForward ? self.active + 1 : self.active - 1);
          self.pauseAutoplay();

          setTimeout(function () {
            self.wheelCooldown = false;
          }, Math.max(250, self.settings.speed * 0.6));
        },
        { passive: false }
      );
    }

    // Touch / swipe
    this.stage.addEventListener(
      'touchstart',
      function (e) {
        self.touchStartX = e.touches[0].clientX;
        self.touchStartY = e.touches[0].clientY;
      },
      { passive: true }
    );

    this.stage.addEventListener(
      'touchend',
      function (e) {
        if (self.touchStartX === null) return;
        var dx = e.changedTouches[0].clientX - self.touchStartX;
        var dy = e.changedTouches[0].clientY - self.touchStartY;

        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) {
            self.go(self.active + 1);
          } else {
            self.go(self.active - 1);
          }
          self.pauseAutoplay();
        }
        self.touchStartX = null;
        self.touchStartY = null;
      },
      { passive: true }
    );

    // Pause autoplay on interaction
    ['mouseenter', 'focusin', 'touchstart'].forEach(function (evt) {
      self.root.addEventListener(evt, function () {
        self.pauseAutoplay();
      }, { passive: true });
    });
    ['mouseleave', 'focusout'].forEach(function (evt) {
      self.root.addEventListener(evt, function () {
        if (self.settings.autoRotate) self.startAutoplay();
      });
    });

    window.addEventListener('resize', function () {
      self.render(true);
    });
  };

  ThreeDShowcase.prototype.observeVisibility = function () {
    var self = this;
    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self.inView = entry.isIntersecting && entry.intersectionRatio > 0.6;
        });
      },
      { threshold: [0, 0.6, 1] }
    );
    observer.observe(this.root);
  };

  ThreeDShowcase.prototype.go = function (index) {
    var count = this.count;
    // Wrap around infinitely — after the last product, go back to the
    // first, and vice versa — instead of stopping at the ends.
    var target = ((index % count) + count) % count;
    if (target === this.active) return;
    this.active = target;
    this.render(false);
  };

  ThreeDShowcase.prototype.startAutoplay = function () {
    var self = this;
    this.pauseAutoplay();
    this.autoplayTimer = setInterval(function () {
      self.go(self.active + 1);
    }, this.settings.autoRotateSpeed * 1000);
  };

  ThreeDShowcase.prototype.pauseAutoplay = function () {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  ThreeDShowcase.prototype.render = function (immediate) {
    var self = this;
    var s = this.settings;
    var maxAngle = 12 + (s.curvature / 100) * 38; // degrees, 12..50
    var radius = s.perspective * 0.32;

    var count = this.count;

    this.items.forEach(function (item, i) {
      var offset = i - self.active;
      // Shortest circular distance, so wrapping from last -> first (or
      // vice versa) swings the short way around the arc, not all the
      // way across every product.
      if (offset > count / 2) offset -= count;
      if (offset < -count / 2) offset += count;
      var absOffset = Math.abs(offset);
      var angleDeg = clamp(offset * maxAngle * 0.55, -maxAngle * 3, maxAngle * 3);
      var rad = (angleDeg * Math.PI) / 180;

      var tx = Math.sin(rad) * radius;
      var tz = (Math.cos(rad) - 1) * radius;
      var scale = Math.max(0.35, 1 - absOffset * s.scaleDiff);
      var rotateY = clamp(-angleDeg * (s.rotationIntensity / 45), -75, 75);
      var opacity = absOffset > 3 ? 0 : 1 - absOffset * 0.28;
      var blur = Math.min(s.blurIntensity, absOffset * (s.blurIntensity / 2));
      var zIndex = 100 - absOffset;

      item.style.transform =
        'translate3d(' + tx + 'px, 0, ' + tz + 'px) rotateY(' + rotateY + 'deg) scale(' + scale + ')';
      item.style.opacity = String(clamp(opacity, 0, 1));
      item.style.filter = blur > 0.05 ? 'blur(' + blur + 'px)' : 'none';
      item.style.zIndex = String(zIndex);

      item.classList.toggle('is-active', offset === 0);
      item.classList.toggle('is-visible', absOffset <= 3);
      item.setAttribute('aria-hidden', offset === 0 ? 'false' : 'true');

      if (immediate) {
        item.style.transitionDuration = '1ms';
        requestAnimationFrame(function () {
          item.style.transitionDuration = '';
        });
      }
    });

    this.dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === self.active);
    });
  };

  function initAll() {
    var roots = document.querySelectorAll('.tds-section');
    roots.forEach(function (root) {
      if (root._tdsInstance) return;
      root._tdsInstance = new ThreeDShowcase(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('.tds-section');
    if (root && !root._tdsInstance) {
      root._tdsInstance = new ThreeDShowcase(root);
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target.closest('.tds-item');
    if (!item) return;
    var root = item.closest('.tds-section');
    if (root && root._tdsInstance) {
      root._tdsInstance.go(parseInt(item.dataset.index, 10));
      root._tdsInstance.pauseAutoplay();
    }
  });
})();
